import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { MainLayout } from '../layouts';
import { GET_ENHANCED_RACE_RESULTS } from '@shared/graphql/queries/getEnhancedResults';
import { GET_MEETING } from '@shared/graphql/queries/meetings';
import { formatDateDisplay } from '../utils';
import { MeetingPerformanceSectionImproved } from '../components/results/MeetingPerformanceSectionImproved';
import { ResultsTable } from '../components/results/ResultsTable';
import { useAnalysisType } from '../contexts/AnalysisTypeContext';
import analyticsService from '../services/analytics';
import '../styles/ResultsPage.css';

const ResultsPage: React.FC = () => {
  // Get parameters from URL
  const { dateString, trackName, trackCode, state, track, date } = useParams<{
    dateString?: string;
    trackName?: string;
    trackCode?: string;
    state?: string;
    track?: string;
    date?: string;
  }>();
  
  // Handle both URL formats
  const finalDateString = dateString || date;
  const finalTrackCode = trackCode || trackName || track; // Support legacy URLs
  
  // Parse date from URL
  const parsedDate = finalDateString ? (() => {
    const year = parseInt(finalDateString.substring(0, 4));
    const month = parseInt(finalDateString.substring(4, 6)) - 1; // Months are 0-indexed in JS
    const day = parseInt(finalDateString.substring(6, 8));
    console.log('Parsing date from URL:', JSON.stringify({ finalDateString, year, month, day }));
    
    // Create date using UTC to avoid timezone issues
    const date = new Date(Date.UTC(year, month, day));
    console.log('Parsed date:', date.toISOString());
    return date;
  })() : new Date();
  
  // State for selected race and view options
  const [selectedRace, setSelectedRace] = useState<number>(1);
  const [showAnalysis, setShowAnalysis] = useState<boolean>(true);
  const [showOnlyPicks, setShowOnlyPicks] = useState<boolean>(false);
  
  // Get current analysis type from context
  const { currentAnalysisType } = useAnalysisType();
  
  // Track if we've already logged a page view for this session
  const hasTrackedPageView = useRef(false);
  
  // Query race results data
  const { loading, error, data } = useQuery(GET_ENHANCED_RACE_RESULTS, {
    variables: {
      date: parsedDate.toISOString().split('T')[0],
      trackName: finalTrackCode,
      analysisTypeId: currentAnalysisType
    },
    skip: !finalDateString || !finalTrackCode
  });
  
  // Query meeting data for performance metrics - use track code directly
  const { data: meetingData } = useQuery(GET_MEETING, {
    variables: {
      meetingId: `${parsedDate.toISOString().split('T')[0].replace(/-/g, '')}-${finalTrackCode}`,
      analysisTypeId: currentAnalysisType
    },
    skip: !finalDateString || !finalTrackCode
  });
  
  console.log('ResultsPage - Query variables:', JSON.stringify({
    date: parsedDate.toISOString().split('T')[0],
    trackCode: finalTrackCode
  }));
  console.log('ResultsPage - Query result - loading:', loading);
  console.log('ResultsPage - Query result - error:', error ? error.message : 'none');
  console.log('ResultsPage - Query result - data:', data ? 'has data' : 'no data');
  if (data?.getEnhancedRaceResults) {
    console.log('ResultsPage - Race results count:', data.getEnhancedRaceResults.length);
    console.log('ResultsPage - First race:', data.getEnhancedRaceResults[0] ? 
      `Race ${data.getEnhancedRaceResults[0].race_number} with ${data.getEnhancedRaceResults[0].results?.length || 0} results` : 
      'No first race');
  }
  
  // Debug meeting data
  console.log('ResultsPage - Meeting data:', meetingData ? 'has meeting data' : 'no meeting data');
  if (meetingData?.meeting) {
    console.log('ResultsPage - Meeting ID:', meetingData.meeting.meeting_id);
    console.log('ResultsPage - Meeting has performanceImproved:', meetingData.meeting.performanceImproved ? 'yes' : 'no');
    console.log('ResultsPage - Meeting keys:', Object.keys(meetingData.meeting));
    console.log('ResultsPage - Meeting data:', JSON.stringify(meetingData.meeting, null, 2));
    if (meetingData.meeting.performanceImproved) {
      console.log('ResultsPage - Meeting performanceImproved:', JSON.stringify(meetingData.meeting.performanceImproved));
    } else {
      console.log('ResultsPage - Meeting performance:', meetingData.meeting.performance ? JSON.stringify(meetingData.meeting.performance) : 'no performance data');
      console.log('ResultsPage - Meeting exoticBets:', meetingData.meeting.exoticBets ? JSON.stringify(meetingData.meeting.exoticBets) : 'no exoticBets data');
    }
  }
  
  // Refs for scrolling to races
  const raceRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  
  // Calculate total performance summary
  const calculateTotalPerformance = () => {
    if (!data?.getEnhancedRaceResults || data.getEnhancedRaceResults.length === 0) {
      return {
        total_runners: 0,
        total_analyzed: 0,
        top_pick_roi: 0,
        value_pick_roi: 0,
        first_pick_wins: 0,
        first_pick_places: 0,
        first_pick_total: 0,
        second_pick_wins: 0,
        second_pick_places: 0,
        second_pick_total: 0,
        third_pick_wins: 0,
        third_pick_places: 0,
        third_pick_total: 0,
        value_pick_wins: 0,
        value_pick_places: 0,
        value_pick_total: 0,
        overall_winners_found: 0,
        overall_races_with_selections: 0,
        overall_placed_selections: 0,
        overall_total_selections: 0
      };
    }
    
    // Initialize counters
    let totalRunners = 0;
    let totalAnalyzed = 0;
    let topPickWins = 0;
    let topPickStakes = 0;
    let valuePickWins = 0;
    let valuePickStakes = 0;
    
    // Initialize strike rate counters
    let firstPickWins = 0;
    let firstPickPlaces = 0;
    let firstPickTotal = 0;
    
    let secondPickWins = 0;
    let secondPickPlaces = 0;
    let secondPickTotal = 0;
    
    let thirdPickWins = 0;
    let thirdPickPlaces = 0;
    let thirdPickTotal = 0;
    
    let valuePickWinsCount = 0;
    let valuePickPlacesCount = 0;
    let valuePickTotal = 0;
    
    let overallWinnersFound = 0;
    let overallRacesWithSelections = 0;
    let overallPlacedSelections = 0;
    let overallTotalSelections = 0;
    
    // Calculate totals
    data.getEnhancedRaceResults.forEach((race: any) => {
      if (!race.results || race.results.length === 0) return;
      
      totalRunners += race.total_runners || 0;
      totalAnalyzed += race.total_analyzed || 0;
      
      // Check if race has any selections
      const hasSelections = race.results.some((r: any) => r.pick_type);
      if (hasSelections) {
        overallRacesWithSelections++;
        
        // Check if we found the winner in our selections
        const winnerFound = race.results.some((r: any) => 
          r.pick_type && (r.position === 1 || r.position_finish === 1)
        );
        if (winnerFound) {
          overallWinnersFound++;
        }
      }
      
      // Process first picks (top pick order 1)
      const firstPick = race.results.find((r: any) => r.pick_type === 'top' && r.pick_order === 1);
      if (firstPick) {
        firstPickTotal++;
        topPickStakes++;
        
        // Check if first pick won
        if (firstPick.position === 1 || firstPick.position_finish === 1) {
          firstPickWins++;
          // a winner still waiting for its SP is pending: no stake yet, rather than a loss
          if (Number(firstPick.price_sp) > 0) topPickWins += Number(firstPick.price_sp);
          else topPickStakes--;
        }
        
        // Check if first pick placed (positions 1-3)
        if ((firstPick.position >= 1 && firstPick.position <= 3) || 
            (firstPick.position_finish >= 1 && firstPick.position_finish <= 3)) {
          firstPickPlaces++;
        }
        
        // Add to overall selections count
        overallTotalSelections++;
        if ((firstPick.position >= 1 && firstPick.position <= 3) || 
            (firstPick.position_finish >= 1 && firstPick.position_finish <= 3)) {
          overallPlacedSelections++;
        }
      }
      
      // Process second picks (top pick order 2)
      const secondPick = race.results.find((r: any) => r.pick_type === 'top' && r.pick_order === 2);
      if (secondPick) {
        secondPickTotal++;
        
        // Check if second pick won
        if (secondPick.position === 1 || secondPick.position_finish === 1) {
          secondPickWins++;
        }
        
        // Check if second pick placed (positions 1-3)
        if ((secondPick.position >= 1 && secondPick.position <= 3) || 
            (secondPick.position_finish >= 1 && secondPick.position_finish <= 3)) {
          secondPickPlaces++;
        }
        
        // Add to overall selections count
        overallTotalSelections++;
        if ((secondPick.position >= 1 && secondPick.position <= 3) || 
            (secondPick.position_finish >= 1 && secondPick.position_finish <= 3)) {
          overallPlacedSelections++;
        }
      }
      
      // Process third picks (top pick order 3)
      const thirdPick = race.results.find((r: any) => r.pick_type === 'top' && r.pick_order === 3);
      if (thirdPick) {
        thirdPickTotal++;
        
        // Check if third pick won
        if (thirdPick.position === 1 || thirdPick.position_finish === 1) {
          thirdPickWins++;
        }
        
        // Check if third pick placed (positions 1-3)
        if ((thirdPick.position >= 1 && thirdPick.position <= 3) || 
            (thirdPick.position_finish >= 1 && thirdPick.position_finish <= 3)) {
          thirdPickPlaces++;
        }
        
        // Add to overall selections count
        overallTotalSelections++;
        if ((thirdPick.position >= 1 && thirdPick.position <= 3) || 
            (thirdPick.position_finish >= 1 && thirdPick.position_finish <= 3)) {
          overallPlacedSelections++;
        }
      }
      
      // Process value picks
      const valuePicks = race.results.filter((r: any) => r.pick_type === 'value');
      if (valuePicks.length > 0) {
        valuePickTotal += valuePicks.length;
        
        // Check for value pick wins
        const valuePickWinner = valuePicks.find((r: any) => 
          r.position === 1 || r.position_finish === 1
        );
        
        if (valuePickWinner) {
          valuePickWinsCount++;
        }
        
        // Check for value pick places
        const valuePickPlacers = valuePicks.filter((r: any) => 
          (r.position >= 1 && r.position <= 3) || 
          (r.position_finish >= 1 && r.position_finish <= 3)
        );
        
        valuePickPlacesCount += valuePickPlacers.length;
        
        // Add to overall selections count
        overallTotalSelections += valuePicks.length;
        overallPlacedSelections += valuePickPlacers.length;
        
        // For ROI calculation (using first value pick)
        const firstValuePick = valuePicks.find((r: any) => r.pick_order === 1);
        if (firstValuePick) {
          valuePickStakes++;
          if (firstValuePick.position === 1 || firstValuePick.position_finish === 1) {
            if (Number(firstValuePick.price_sp) > 0) valuePickWins += Number(firstValuePick.price_sp);
            else valuePickStakes--;
          }
        }
      }
    });
    
    // Calculate ROIs
    const topPickROI = topPickStakes > 0 ? ((topPickWins / topPickStakes) - 1) * 100 : 0;
    const valuePickROI = valuePickStakes > 0 ? ((valuePickWins / valuePickStakes) - 1) * 100 : 0;
    
    return {
      total_runners: totalRunners,
      total_analyzed: totalAnalyzed,
      top_pick_roi: topPickROI,
      value_pick_roi: valuePickROI,
      first_pick_wins: firstPickWins,
      first_pick_places: firstPickPlaces,
      first_pick_total: firstPickTotal,
      second_pick_wins: secondPickWins,
      second_pick_places: secondPickPlaces,
      second_pick_total: secondPickTotal,
      third_pick_wins: thirdPickWins,
      third_pick_places: thirdPickPlaces,
      third_pick_total: thirdPickTotal,
      value_pick_wins: valuePickWinsCount,
      value_pick_places: valuePickPlacesCount,
      value_pick_total: valuePickTotal,
      overall_winners_found: overallWinnersFound,
      overall_races_with_selections: overallRacesWithSelections,
      overall_placed_selections: overallPlacedSelections,
      overall_total_selections: overallTotalSelections
    };
  };
  
  // Get total performance summary
  const totalPerformance = calculateTotalPerformance();
  
  // Track page view when data loads
  useEffect(() => {
    if (!hasTrackedPageView.current && data?.getEnhancedRaceResults && !loading && finalTrackCode && finalDateString) {
      try {
        const selectedDate = finalDateString ?
          `${finalDateString.substring(0, 4)}-${finalDateString.substring(4, 6)}-${finalDateString.substring(6, 8)}` :
          new Date().toISOString().split('T')[0];
        
        // Get track name from results for analytics
        const trackName = data.getEnhancedRaceResults[0]?.track_name || finalTrackCode;
        
        analyticsService.trackPageView(
          'results',
          trackName,
          selectedDate,
          {
            raceCount: data.getEnhancedRaceResults.length,
            hasResults: data.getEnhancedRaceResults.some((race: any) => race.results?.length > 0),
            hasMeetingData: !!meetingData?.meeting,
            totalRunners: totalPerformance.total_runners,
            totalAnalyzed: totalPerformance.total_analyzed,
            overallWinnersFound: totalPerformance.overall_winners_found
          }
        );
        
        hasTrackedPageView.current = true;
        console.log('📊 Analytics: ResultsPage view tracked for', trackName, selectedDate);
      } catch (error) {
        console.error('Analytics error:', error);
        // Don't let analytics errors break the app
      }
    }
  }, [data, loading, finalTrackCode, finalDateString, meetingData, totalPerformance]);
  
  // Reset tracking flag when key parameters change
  useEffect(() => {
    hasTrackedPageView.current = false;
  }, [finalTrackCode, finalDateString]);
  
  // Effect to scroll to selected race when it changes
  useEffect(() => {
    if (selectedRace && raceRefs.current[selectedRace]) {
      raceRefs.current[selectedRace]?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [selectedRace]);
  
  // Analytics functions
  const handleShowAnalysisChange = (checked: boolean) => {
    setShowAnalysis(checked);
    
    // Track analytics
    try {
      if (finalTrackCode && finalDateString) {
        const selectedDate = finalDateString ?
          `${finalDateString.substring(0, 4)}-${finalDateString.substring(4, 6)}-${finalDateString.substring(6, 8)}` :
          new Date().toISOString().split('T')[0];
        
        const trackName = data?.getEnhancedRaceResults?.[0]?.track_name || finalTrackCode;
        
        analyticsService.trackEvent('checkbox_toggled', {
          pageType: 'results',
          trackName: trackName,
          selectedDate,
          metadata: {
            checkboxType: 'show_analysis',
            newValue: checked,
            previousValue: !checked
          }
        });
      }
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };
  
  const handleShowOnlyPicksChange = (checked: boolean) => {
    setShowOnlyPicks(checked);
    
    // Track analytics
    try {
      if (finalTrackCode && finalDateString) {
        const selectedDate = finalDateString ?
          `${finalDateString.substring(0, 4)}-${finalDateString.substring(4, 6)}-${finalDateString.substring(6, 8)}` :
          new Date().toISOString().split('T')[0];
        
        const trackName = data?.getEnhancedRaceResults?.[0]?.track_name || finalTrackCode;
        
        analyticsService.trackEvent('checkbox_toggled', {
          pageType: 'results',
          trackName: trackName,
          selectedDate,
          metadata: {
            checkboxType: 'show_only_picks',
            newValue: checked,
            previousValue: !checked
          }
        });
      }
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };
  
  const handleRaceNavigation = (raceNumber: number) => {
    setSelectedRace(raceNumber);
    
    // Track analytics
    try {
      if (finalTrackCode && finalDateString) {
        const selectedDate = finalDateString ?
          `${finalDateString.substring(0, 4)}-${finalDateString.substring(4, 6)}-${finalDateString.substring(6, 8)}` :
          new Date().toISOString().split('T')[0];
        
        const trackName = data?.getEnhancedRaceResults?.[0]?.track_name || finalTrackCode;
        
        analyticsService.trackEvent('race_navigation', {
          pageType: 'results',
          trackName: trackName,
          raceNumber,
          selectedDate,
          metadata: {
            previousRace: selectedRace,
            navigationMethod: 'button_click'
          }
        });
      }
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };
  
  console.log('ResultsPage - Selected race number:', selectedRace);
  
  // Function to get result class for styling
  const getResultClass = (result: any) => {
    const classes = [];
    
    // First highlight position (1st, 2nd, 3rd)
    if (result.position === 1) {
      classes.push('bg-racing-green text-white');
    } else if (result.position === 2) {
      classes.push('bg-racing-blue-light text-white');
    } else if (result.position === 3) {
      classes.push('bg-yellow-600 text-white'); // Changed to yellow for better contrast
    }
    
    // Then highlight our picks based on their performance
    if (result.pick_type) {
      if (result.position === 1) {
        // Winner - gold border
        classes.push('border-l-4 border-yellow-400');
      } else if (result.position === 2 || result.position === 3) {
        // Place - silver border
        classes.push('border-l-4 border-gray-300');
      } else {
        // Loser - bronze/brown border
        classes.push('border-l-4 border-yellow-800');
      }
      
      // Add a badge for pick type and order
      if (result.pick_type === 'top') {
        classes.push('top-pick');
      } else if (result.pick_type === 'value') {
        classes.push('value-pick');
      }
    }
    
    return classes.join(' ');
  };

  // Function to format ROI display
  const formatROI = (roi: number) => {
    const color = roi >= 0 ? 'text-racing-green' : 'text-racing-red';
    return <span className={color}>{roi >= 0 ? '+' : ''}{roi.toFixed(1)}%</span>;
  };
  
  if (loading) {
    return (
      <MainLayout>
        <div className="p-4">
          <div className="bg-white p-6 rounded-lg shadow-card text-center">
            <p className="text-gray-500">Loading race results...</p>
          </div>
        </div>
      </MainLayout>
    );
  }
  
  if (error) {
    return (
      <MainLayout>
        <div className="p-4">
          <div className="bg-white p-6 rounded-lg shadow-card text-center">
            <p className="text-racing-red">Error loading race results: {error.message}</p>
            <Link to="/" className="mt-4 inline-block bg-deep-navy text-white px-4 py-2 rounded hover:bg-racing-green">
              Return to Home
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }
  
  return (
    <MainLayout>
      <div className="results-page p-4">
        {/* Back to Meetings Link */}
        <div className="mb-4">
          <Link 
            to={`/${finalDateString}`} 
            className="text-deep-navy hover:text-racing-green"
          >
            ← Back to Meetings
          </Link>
        </div>
        
        {/* Track Header */}
        <div className="bg-white rounded-lg shadow-card mb-6">
          <div className="p-4 border-b border-racing-gray-darker">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">{data?.getEnhancedRaceResults?.[0]?.track_name?.toUpperCase() || finalTrackCode?.toUpperCase()} Race Results</h1>
              <p className="text-gray-600">{formatDateDisplay(parsedDate)}</p>
            </div>
            <Link
              to={`/${finalDateString ? finalDateString.substring(0, 4) : ''}/${finalTrackCode?.toLowerCase() || ''}/${finalDateString || ''}/tips`}
              className="bg-deep-navy text-white px-4 py-2 rounded hover:bg-racing-green"
            >
              View Tips
            </Link>
          </div>
          </div>
        </div>
        
        {/* View Options */}
        <div className="bg-white rounded-lg shadow-card mb-6 p-4">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showAnalysis}
                onChange={(e) => handleShowAnalysisChange(e.target.checked)}
                className="form-checkbox text-deep-navy"
              />
              <span>Show Analysis</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showOnlyPicks}
                onChange={(e) => handleShowOnlyPicksChange(e.target.checked)}
                className="form-checkbox text-deep-navy"
              />
              <span>Show Only Picks</span>
            </label>
          </div>
        </div>
        
        {/* Meeting Performance from GraphQL */}
        {meetingData?.meeting && (
          <MeetingPerformanceSectionImproved meeting={meetingData.meeting} />
        )}
        
        {/* Race Navigation - Sticky */}
        {data?.getEnhancedRaceResults && data.getEnhancedRaceResults.length > 0 && (
          <div className="bg-white rounded-lg shadow-card mb-6 p-4 sticky top-0 z-10">
            <h2 className="text-lg font-bold mb-3">Race Navigation</h2>
            <div className="flex flex-wrap gap-2">
              {data.getEnhancedRaceResults.map((race: any) => (
                <button
                  key={race.race_number}
                  className={`px-3 py-1 rounded ${selectedRace === race.race_number ? 'bg-deep-navy text-white' : 'bg-racing-gray hover:bg-racing-gray-dark'}`}
                  onClick={() => handleRaceNavigation(race.race_number)}
                >
                  Race {race.race_number}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* All Races Results */}
        {data?.getEnhancedRaceResults && data.getEnhancedRaceResults.map((race: any) => (
          <div 
            key={race.race_id}
            ref={el => {
              if (el) raceRefs.current[race.race_number] = el;
            }}
            className={`bg-white rounded-lg shadow-card mb-6 ${selectedRace === race.race_number ? 'ring-2 ring-deep-navy' : ''}`}
            id={`race-${race.race_number}`}
          >
            <div className="bg-deep-navy text-white p-3 rounded-t-lg">
              <h2 className="text-lg font-heading font-bold">Race {race.race_number} - {race.race_name}</h2>
              <div className="flex flex-wrap gap-2 mt-1">
                <span className="bg-racing-green px-2 py-0.5 rounded text-sm">{race.distance}m</span>
                <span className="bg-racing-green px-2 py-0.5 rounded text-sm">{race.class}</span>
                <span className="bg-racing-green px-2 py-0.5 rounded text-sm">
                  {new Date(race.race_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </span>
              </div>
            </div>
            
            <div className="p-4">
              {/* Results Table */}
              {race.results && race.results.length > 0 ? (
                <div className="overflow-x-auto">
                  <ResultsTable 
                    results={race.results.filter((result: any) => !showOnlyPicks || result.pick_type)}
                    className="w-full"
                  />
                  
                  {/* Analysis Section (if enabled) */}
                  {showAnalysis && (
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full compact-analysis-table">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Pos</th>
                            <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Horse</th>
                            <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Pick Type</th>
                            <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Assessed</th>
                            <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                          </tr>
                        </thead>
                        <tbody>
                          {race.results
                            .filter((result: any) => !showOnlyPicks || result.pick_type)
                            .filter((result: any) => result.analysis_summary)
                            .map((result: any, index: number) => (
                              <tr 
                                key={`analysis-${result.runner_id}`} 
                                className={`${index % 2 === 0 ? 'bg-racing-gray' : ''}`}
                              >
                                <td className="p-2">
                                  {result.position !== undefined && result.position !== 0 ? (
                                    <div className={`${result.position === 1 ? 'bg-green-500' : result.position === 2 ? 'bg-blue-500' : result.position === 3 ? 'bg-blue-400' : 'bg-gray-400'} text-white rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs`}>
                                      {result.position}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="p-2 font-medium">{result.horse_name}</td>
                                <td className="p-2">
                                  {result.pick_type === 'top' && (
                                    <span className="bg-deep-navy text-white px-2 py-0.5 rounded mr-1">
                                      Top {result.pick_order}
                                    </span>
                                  )}
                                  {result.pick_type === 'value' && (
                                    <span className="bg-racing-green text-white px-2 py-0.5 rounded">
                                      Value
                                    </span>
                                  )}
                                </td>
                                <td className="p-2">${result.assessed_odds?.toFixed(2)}</td>
                                <td className="p-2">{result.confidence}%</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center p-4">
                  <p className="text-gray-500">No results available for this race.</p>
                </div>
              )}
              
              {/* Individual Race Performance Summary */}
              <div className="mt-6 pt-4 border-t border-racing-gray-darker">
                <h3 className="text-md font-bold mb-2">Race Performance</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Runners</p>
                    <p className="font-bold">{race.total_runners}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Analyzed</p>
                    <p className="font-bold">{race.total_analyzed}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Top Pick ROI</p>
                    <p className="font-bold">{formatROI(race.top_pick_roi)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Value Pick ROI</p>
                    <p className="font-bold">{formatROI(race.value_pick_roi)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </MainLayout>
  );
};

export default ResultsPage;
